import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { DBLessonTemplateSchema, LessonTemplateSchema } from './schemas';

type LessonTemplate = z.infer<typeof LessonTemplateSchema>;

export const LessonTemplateCodecConfig: TransformerConfig<
  typeof LessonTemplateSchema,
  typeof DBLessonTemplateSchema,
  'id'
> = {
  name: 'LessonTemplate',
  clientSchema: LessonTemplateSchema,
  dbSchema: DBLessonTemplateSchema,
  transformers: [
    createRenameTransformer<typeof LessonTemplateSchema, typeof DBLessonTemplateSchema>(
      'courseId',
      'course'
    ),
    createRenameTransformer<typeof LessonTemplateSchema, typeof DBLessonTemplateSchema>(
      'code',
      'lessonId'
    ),
    createRenameTransformer<typeof LessonTemplateSchema, typeof DBLessonTemplateSchema>(
      'questionSetId',
      'quiz'
    ),
    // permissionIds: [permissionId] ↔ permissions: { permissionId: {} }
    createTransformer<typeof LessonTemplateSchema, typeof DBLessonTemplateSchema>({
      encoder: {
        sourceFields: ['permissionIds'] as const,
        encode: ({ permissionIds }) => ({
          permissions: Object.fromEntries(
            (permissionIds as LessonTemplate['permissionIds']).map(x => [x, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['permissions'] as const,
        decode: ({ permissions = {} }) => ({
          permissionIds: Object.keys(permissions),
        }),
      },
    }),
  ],
  derived: { id: template => buildRecordId('lessonTemplate', template) },
};

export const encodeLessonTemplateToDb = createEncodeToDbFn(LessonTemplateCodecConfig);
export const decodeLessonTemplateFromDb = createDecodeFromDbFn(LessonTemplateCodecConfig);
